import { Button } from 'components/atoms';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiUserCircle } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetAccounts } from 'services/account';
import { useGetUnreadThreadsCount } from 'services/chat';
import { useGetClientModuleConfiguration } from 'services/configuration';
import { INavbarItem } from 'types/navbar/navbar.interface';
import { getParentPathname } from 'utils/navbar/getParentPathname';
import { navbarItems } from 'utils/navbar/navbarItems';
import './Navbar.scss';

const getMainCategoryIdFromNavbarItem = (item: INavbarItem): string => {
  return item.path as string;
};

const getDefaultSubroute = (item: INavbarItem) => {
  const firstChild = item.children?.[0];
  if (!firstChild) return `/${item.path}`;
  return `/${firstChild.path}`;
};

const Navbar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const configurationModule = useGetClientModuleConfiguration();

  const { tokenData, lastSubrouteByCategory } = useStoreState();

  const [activeGroup, setActiveGroup] = useState<INavbarItem | null>();

  useEffect(() => {
    !activeGroup &&
      setActiveGroup(
        navbarItems.find(
          (item) =>
            getParentPathname(pathname) === item.path ||
            item.children?.find((child) => child.path === getParentPathname(pathname)),
        ) ?? null,
      );
  }, [pathname]);

  const isPageActive = (name: string): boolean => getParentPathname(pathname) === name;

  const onClickGroup = (item?: INavbarItem) => {
    if (!item) return;

    const mainCategory = getMainCategoryIdFromNavbarItem(item);

    const last = lastSubrouteByCategory?.[mainCategory];

    const defaultPath = getDefaultSubroute(item);

    const target = last ?? defaultPath;

    setActiveGroup(item);
    navigate(target);
  };

  const accounts = useGetAccounts();

  const unreadCount = useGetUnreadThreadsCount();

  return (
    <div className="navbar-wrapper">
      <div className="navbar">
        <div className="navbar-logo">
          <img
            src={
              configurationModule.data?.currentLogoPath ??
              `${process.env.PUBLIC_URL}/icons/logo.svg`
            }
            alt=""
          />
          <div className="flex flex-col w-max">
            <p className="text-lg font-bold w-max">
              {configurationModule.data?.clientName ?? 'MagelUP'}
            </p>
          </div>
        </div>
        <div className="navbar-buttons">
          {navbarItems.map(
            (item, key) =>
              !item.hidden && (
                <Button
                  isClear
                  isActive={activeGroup?.path === item.path}
                  onClick={() => onClickGroup(item)}
                  className="button"
                >
                  <Button.Icon
                    icon={
                      activeGroup?.path === item.path && item.fillIcon ? (
                        <item.fillIcon />
                      ) : (
                        <item.icon />
                      )
                    }
                  />
                  <Button.Text text={t(`navbar.${item.name ?? item.path}`)} />
                </Button>
              ),
          )}
        </div>
        <div className="navbar-account">
          <Button
            isClear
            onlyIcon
            onClick={() => {
              onClickGroup(navbarItems.find((item) => item.path === 'settings'));
              navigate(`/label`);
            }}
          >
            <Button.Icon icon={<PiUserCircle />} />
            <Button.Text
              text={accounts.data?.find((a) => a.id === tokenData?.CurrentAccount)?.labelName ?? ''}
            />
            {!!unreadCount.data && <Button.Label>{unreadCount.data}</Button.Label>}
          </Button>
        </div>
      </div>
      {!!activeGroup?.children?.length && (
        <div className="navbar">
          <div className="navbar-buttons">
            {activeGroup?.children.map((item, key) => (
              <Button
                isClear
                size="compact"
                isActive={isPageActive(item.path)}
                onClick={() => navigate(`/${item.path}`)}
                className="button"
              >
                <Button.Icon
                  icon={
                    isPageActive(item.path) && item.fillIcon ? <item.fillIcon /> : <item.icon />
                  }
                />
                <Button.Text text={t(`navbar.${item.name ?? item.path}`)} />
                {!!unreadCount.data && item.path === 'support' && (
                  <Button.Label>{unreadCount.data}</Button.Label>
                )}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
